import { useMemo, useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/shared/components/ui/carousel';
import { AccountCard } from './AccountCard';
import WalletIcon from '@/shared/assets/wallet-icon.svg?react';
import InvestAccountIcon from '@/shared/assets/invest-account-icon.svg?react';
import SpotAccountIcon from '@/shared/assets/spot-account-icon.svg?react';
import PercentIcon from '@/shared/assets/percent-icon.svg?react';
import EarnAccountIcon from '@/shared/assets/earn-account-icon.svg?react';
import { useTranslation } from 'react-i18next';
import LogoSmallIcon from '@/shared/assets/f2ex-logo-small.svg?react';
import AccountsCarouselControlButton from './AccountsCarouselControlButton';
import { AccountCardCreateNew } from './AccountCardCreateNew';
import { AccountCardContentSkeleton } from './AccountCardContentSkeleton';
import type { AccountSectionCardType } from '@/shared/types';
import {
  usePendingAccountsStore,
  useOpenAccountAvailabilityStore,
} from '@/features/account-open';
import {
  useAccountsStore,
  useAccountsConfigurations,
  sortConfigurationTypes,
  type AccountsRestsByType,
  type AccountType,
} from '@/entities/account';
import { cn } from '@/shared/utils';
import { useAppSettingsStore } from '@/shared/lib';
import { pendingCardsComparator } from '../../lib';
import { sortByDate } from '@/shared/lib/sorting';
import { useNavigate } from 'react-router-dom';

type Props = {
  isBalancesHidden: boolean;
  isLoading: boolean;
  onCreateAccount: () => void;
  onAccountCardClick: (accountCard: AccountSectionCardType) => void;
  isAccountCardSelected: (accountType: AccountType) => boolean;
  onDeposit: () => void;
  onWithdraw: () => void;
  onInternalTransfer: (context?: {
    accountType: AccountType;
    accountNumber: string;
    assetCode: string;
    direction?: 'from' | 'to';
  }) => void;
  accountsRests: AccountsRestsByType;
  onAccountClick: (accountNumber: string) => void;
};

const AccountsCarousel = ({
  isBalancesHidden,
  isLoading,
  onCreateAccount,
  onAccountCardClick,
  isAccountCardSelected,
  onDeposit,
  onWithdraw,
  onInternalTransfer,
  accountsRests,
  onAccountClick,
}: Props) => {
  const { t } = useTranslation();
  const { ewallet, invest, spot, leverage, earn } = useAccountsStore();
  const { pendingAccounts } = usePendingAccountsStore();
  const { codesAvailability } = useOpenAccountAvailabilityStore();
  const { baseAsset } = useAppSettingsStore();
  const { accountsConfigurations } = useAccountsConfigurations();

  const navigate = useNavigate();

  const [api, setApi] = useState<CarouselApi | undefined>(undefined);

  const accountsCardsByType: Array<AccountSectionCardType> = useMemo(() => {
    const cards: Array<AccountSectionCardType> = [
      {
        type: 'ewallet',
        title: t('personalAccount.accountsSection.ewalletTitle'),
        icon: WalletIcon,
        label: LogoSmallIcon,
        data: ewallet,
        isPending: false,
        exists: !!ewallet && ewallet.accounts.length > 0,
        created: ewallet?.created || '',
      },
      {
        type: 'invest',
        title: t('personalAccount.accountsSection.investTitle'),
        icon: InvestAccountIcon,
        label: LogoSmallIcon,
        data: invest,
        isPending: pendingAccounts.some(
          account => account.accountType === 'invest'
        ),
        exists: !!invest && invest.accounts.length > 0,
        created: invest?.created || '',
      },
      {
        type: 'spot',
        title: t('personalAccount.accountsSection.spotTitle'),
        icon: SpotAccountIcon,
        label: LogoSmallIcon,
        data: spot,
        isPending: pendingAccounts.some(
          account => account.accountType === 'spot'
        ),
        exists: !!spot && spot.accounts.length > 0,
        created: spot?.created || '',
      },
      {
        type: 'leverage',
        title: t('personalAccount.accountsSection.leverageTitle'),
        icon: PercentIcon,
        label: LogoSmallIcon,
        data: leverage,
        isPending: pendingAccounts.some(
          account => account.accountType === 'margin'
        ),
        exists: !!leverage && leverage.accounts.length > 0,
        created: leverage?.created || '',
      },
      {
        type: 'earn',
        title: t('personalAccount.accountsSection.earnTitle'),
        icon: EarnAccountIcon,
        label: LogoSmallIcon,
        data: earn,
        isPending: pendingAccounts.some(
          account => account.accountType === 'earn'
        ),
        exists: !!earn && earn.accounts.length > 0,
        created: earn?.created || '',
      },
    ];

    if (!accountsConfigurations) {
      return [...cards]
        .sort(pendingCardsComparator)
        .sort((a, b) => sortByDate('asc')(a.created, b.created));
    }

    const orderedTypeCodes = sortConfigurationTypes(
      accountsConfigurations.accountTypes
    ).map(type => type.accountTypeCode.toLowerCase());

    const ewalletCards = cards.filter(card => card.type === 'ewallet');
    const configuredCards = cards
      .filter(
        card => card.type !== 'ewallet' && orderedTypeCodes.includes(card.type)
      )
      .sort(
        (a, b) =>
          orderedTypeCodes.indexOf(a.type) - orderedTypeCodes.indexOf(b.type)
      );
    const unknownCards = cards
      .filter(
        card => card.type !== 'ewallet' && !orderedTypeCodes.includes(card.type)
      )
      .sort((a, b) => sortByDate('asc')(a.created, b.created));

    return [...ewalletCards, ...configuredCards, ...unknownCards];
  }, [
    t,
    ewallet,
    invest,
    spot,
    leverage,
    earn,
    pendingAccounts,
    accountsConfigurations,
  ]);

  const offerableTypeCodes = useMemo(() => {
    if (!accountsConfigurations) {
      return [];
    }

    return sortConfigurationTypes(accountsConfigurations.accountTypes)
      .map(accountType => accountType.accountTypeCode.toLowerCase())
      .filter(typeCode => {
        const card = accountsCardsByType.find(item => item.type === typeCode);
        if (!card || card.exists || card.isPending) {
          return false;
        }
        return codesAvailability.some(
          code => code.accountTypeCode === typeCode && code.canCreate
        );
      });
  }, [accountsConfigurations, accountsCardsByType, codesAvailability]);

  const canCreateAnyAccount = offerableTypeCodes.length > 0;

  const createAccountDescription = useMemo(() => {
    const names = offerableTypeCodes.map(typeCode =>
      t(`personalAccount.accountsSection.${typeCode}Title`)
    );

    if (names.length <= 1) {
      return names.join('');
    }

    const or = t('common.or').toLowerCase();
    return `${names.slice(0, -1).join(', ')} ${or} ${names[names.length - 1]}`;
  }, [t, offerableTypeCodes]);

  const hasOnlyOneAvailableAccount = useMemo(() => {
    const availableAccounts = accountsCardsByType.filter(
      account => account.exists || account.isPending
    );

    return availableAccounts.length === 1;
  }, [accountsCardsByType]);

  const onExchangeClick = () => {
    navigate('/exchange');
  };

  return (
    <Carousel
      setApi={setApi}
      opts={{
        align: 'start',
      }}
      className="w-full">
      <CarouselContent
        wrapperClassName="pt-[2rem] overflow-visible"
        className={cn('gap-[2.4rem]', isLoading && '-ml-0')}>
        {isLoading ? (
          <AccountCardContentSkeleton />
        ) : (
          accountsCardsByType.map((accountCard, index) => {
            const isPending = pendingAccounts.some(
              account =>
                account.accountType === accountCard.type ||
                (account.accountType === 'margin' &&
                  accountCard.type === 'leverage')
            );
            const isVisible = accountCard.exists || isPending;

            if (!isVisible) {
              return null;
            }

            const { balance, deltaPercent } = accountCard.data || {
              balance: null,
              deltaPercent: null,
            };

            return (
              <CarouselItem key={accountCard.type} className="basis-[30rem]">
                <AccountCard
                  isBalancesHidden={isBalancesHidden}
                  type={accountCard.type}
                  accountTitle={accountCard.title}
                  balance={balance}
                  deltaPercent={deltaPercent}
                  isPending={isPending}
                  currency={baseAsset}
                  canBeSelected={!hasOnlyOneAvailableAccount}
                  onAccountCardClick={
                    hasOnlyOneAvailableAccount
                      ? undefined
                      : () => {
                          onAccountCardClick(accountCard);

                          if (!api) return;

                          const viewportNode = api.rootNode();
                          const slideNodes = api.slideNodes();
                          const slide = slideNodes[index];

                          if (!slide || !viewportNode) return;

                          const containerRect =
                            viewportNode.getBoundingClientRect();
                          const slideRect = slide.getBoundingClientRect();

                          const isFullyVisible =
                            slideRect.left >= containerRect.left &&
                            slideRect.right <= containerRect.right;

                          if (isFullyVisible) return;

                          api.scrollTo(index);
                        }
                  }
                  isAccountCardSelected={
                    hasOnlyOneAvailableAccount
                      ? false
                      : isAccountCardSelected(accountCard.type)
                  }
                  onDeposit={onDeposit}
                  onWithdraw={onWithdraw}
                  onInternalTransfer={onInternalTransfer}
                  onExchange={onExchangeClick}
                  onAccountClick={onAccountClick}
                  accounts={
                    accountCard.type === 'leverage' ||
                    accountCard.type === 'invest' ||
                    accountCard.type === 'earn'
                      ? (accountsRests?.[accountCard.type]?.accounts ?? [])
                      : (accountsRests?.[accountCard.type]?.flatMap(
                          account => account.accounts
                        ) ?? [])
                  }
                />
              </CarouselItem>
            );
          })
        )}
        {canCreateAnyAccount && (
          <CarouselItem className="basis-[30rem]">
            <AccountCardCreateNew
              onClick={onCreateAccount}
              disabled={isLoading}
              description={createAccountDescription}
            />
          </CarouselItem>
        )}
        <CarouselItem className="basis-[10rem]" />
      </CarouselContent>

      <AccountsCarouselControlButton type="previous" />
      <AccountsCarouselControlButton type="next" />
    </Carousel>
  );
};

export { AccountsCarousel };
